var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { Button } from '../../UI/Button';
import { Badge } from '../../UI/Badge';
import { X, ChevronLeft, ChevronRight, SkipForward, Target, Lightbulb } from 'lucide-react';
import { cn } from '../../../lib/utils';
var TAB_TOUR_STEPS = [
    {
        id: 'welcome',
        target: 'body',
        title: 'Welcome to Enhanced Document Analysis!',
        content: 'Let\'s take a quick tour of all the powerful learning tools available in your document analysis system. This will only take a minute.',
        position: 'center'
    },
    {
        id: 'upload',
        target: '[data-tour="upload-button"]',
        title: 'Upload Documents',
        content: 'Start by uploading your documents here. You can drag and drop files directly or click to browse. We support PDF, DOCX, TXT and more.',
        position: 'bottom'
    },
    {
        id: 'overview',
        target: '[data-tab="overview"]',
        title: 'Document Overview',
        content: 'Get a comprehensive summary of your documents with key insights, concepts, and reading time estimates.',
        position: 'bottom'
    },
    {
        id: 'ai-tutor',
        target: '[data-tab="chat"]',
        title: 'AI Tutor',
        content: 'Chat with our intelligent AI tutor for personalized help, explanations, and answers to your questions about the documents.',
        position: 'bottom',
        action: { type: 'click' }
    },
    {
        id: 'flashcards',
        target: '[data-tab="flashcards"]',
        title: 'Flashcards',
        content: 'Generate interactive flashcards automatically from your documents to help with memorization and study sessions.',
        position: 'bottom'
    },
    {
        id: 'quiz',
        target: '[data-tab="quiz"]',
        title: 'Quiz Games',
        content: 'Test your knowledge with fun, interactive quiz games generated from your document content.',
        position: 'bottom'
    },
    {
        id: 'podcast',
        target: '[data-tab="podcast"]',
        title: 'Audio Podcast',
        content: 'Listen to AI-generated podcast episodes that summarize and explain your documents in an engaging audio format.',
        position: 'bottom'
    },
    {
        id: 'mindmap',
        target: '[data-tab="mindmap"]',
        title: 'Mind Maps',
        content: 'Explore visual mind maps that show connections between concepts and ideas from your documents.',
        position: 'bottom'
    },
    {
        id: 'tts',
        target: '[data-tab="tts"]',
        title: 'Text-to-Speech',
        content: 'Convert any document text into natural-sounding speech. Perfect for hands-free learning or accessibility.',
        position: 'bottom'
    },
    // {
    //   id: 'progress',
    //   target: '[data-tour="progress-indicator"]',
    //   title: 'Progress Tracking',
    //   content: 'Monitor the generation progress of your study materials and see when everything is ready to use.',
    //   position: 'top'
    // },
    {
        id: 'complete',
        target: 'body',
        title: 'You\'re All Set!',
        content: 'Now you know how to use all the powerful learning tools. Upload a document and explore each tab to enhance your learning experience!',
        position: 'center'
    }
];
var getElementPosition = function (element) {
    var rect = element.getBoundingClientRect();
    return {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        height: rect.height,
        bottom: rect.bottom + window.scrollY,
        right: rect.right + window.scrollX
    };
};
export var GuidedTour = function (_a) {
    var steps = _a.steps, isOpen = _a.isOpen, onClose = _a.onClose, onComplete = _a.onComplete, className = _a.className;
    var _b = useState(0), currentStep = _b[0], setCurrentStep = _b[1];
    var _c = useState({ x: 0, y: 0, transformX: '-50%', transformY: '0%' }), tooltipPosition = _c[0], setTooltipPosition = _c[1];
    var _d = useState({ x: 0, y: 0, width: 0, height: 0 }), highlightPosition = _d[0], setHighlightPosition = _d[1];
    var _e = useState(false), isMobile = _e[0], setIsMobile = _e[1];
    var overlayRef = useRef(null);
    var step = steps[currentStep];
    var isLastStep = currentStep === steps.length - 1;
    var isFirstStep = currentStep === 0;
    // Detect mobile viewport
    useEffect(function () {
        var checkMobile = function () {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return function () { return window.removeEventListener('resize', checkMobile); };
    }, []);
    useEffect(function () {
        if (!isOpen || !step)
            return;
        var updatePositions = function () {
            if (step.target === 'body') {
                // Center modal for welcome step
                setTooltipPosition({
                    x: window.innerWidth / 2,
                    y: window.innerHeight / 2,
                    transformX: '-50%',
                    transformY: '-50%'
                });
                setHighlightPosition({ x: 0, y: 0, width: 0, height: 0 });
                return;
            }
            var targetElement = document.querySelector(step.target);
            if (!targetElement)
                return;
            var pos = getElementPosition(targetElement);
            // Set highlight position
            setHighlightPosition({
                x: pos.left - 8,
                y: pos.top - 8,
                width: pos.width + 16,
                height: pos.height + 16
            });
            // Calculate tooltip position with boundary checking
            var tooltipWidth = isMobile ? 280 : 384; // w-70 (280px) for mobile, max-w-sm (384px) for desktop
            var tooltipHeight = 200; // Approximate height
            var margin = 16;
            var tooltipX = pos.left + pos.width / 2;
            var tooltipY = pos.top;
            var transformX = '-50%';
            var transformY = '0%';
            // Mobile-specific positioning
            if (isMobile) {
                // On mobile, prefer bottom positioning and center horizontally
                tooltipX = window.innerWidth / 2;
                tooltipY = pos.bottom + margin;
                transformX = '-50%';
                transformY = '0%';
                // If bottom would go off-screen, try top
                if (tooltipY + tooltipHeight > window.innerHeight) {
                    tooltipY = pos.top - margin;
                    transformY = '-100%';
                }
                // If still off-screen, center vertically
                if (tooltipY < margin || tooltipY + tooltipHeight > window.innerHeight) {
                    tooltipY = window.innerHeight / 2;
                    transformY = '-50%';
                }
            }
            else {
                // Desktop positioning with boundary checking
                switch (step.position) {
                    case 'bottom':
                        tooltipY = pos.bottom + margin;
                        transformY = '0%';
                        break;
                    case 'top':
                        tooltipY = pos.top - margin;
                        transformY = '-100%';
                        break;
                    case 'left':
                        tooltipX = pos.left - margin;
                        tooltipY = pos.top + pos.height / 2;
                        transformX = '-100%';
                        transformY = '-50%';
                        break;
                    case 'right':
                        tooltipX = pos.right + margin;
                        tooltipY = pos.top + pos.height / 2;
                        transformX = '0%';
                        transformY = '-50%';
                        break;
                    default:
                        tooltipY = pos.bottom + margin;
                        transformY = '0%';
                }
                // Boundary checking for desktop
                // Check horizontal boundaries
                if (tooltipX - tooltipWidth / 2 < margin) {
                    tooltipX = tooltipWidth / 2 + margin;
                    transformX = '0%';
                }
                else if (tooltipX + tooltipWidth / 2 > window.innerWidth - margin) {
                    tooltipX = window.innerWidth - tooltipWidth / 2 - margin;
                    transformX = '-100%';
                }
                // Check vertical boundaries
                if (tooltipY < margin) {
                    tooltipY = pos.bottom + margin;
                    transformY = '0%';
                }
                else if (tooltipY + tooltipHeight > window.innerHeight - margin) {
                    tooltipY = pos.top - margin;
                    transformY = '-100%';
                }
            }
            setTooltipPosition({ x: tooltipX, y: tooltipY, transformX: transformX, transformY: transformY });
            // Scroll element into view with better mobile handling
            targetElement.scrollIntoView({
                behavior: 'smooth',
                block: isMobile ? 'center' : 'center',
                inline: 'center'
            });
        };
        updatePositions();
        // Update positions on resize and scroll
        window.addEventListener('resize', updatePositions);
        window.addEventListener('scroll', updatePositions);
        return function () {
            window.removeEventListener('resize', updatePositions);
            window.removeEventListener('scroll', updatePositions);
        };
    }, [isOpen, step, currentStep, isMobile]);
    // Keyboard navigation
    useEffect(function () {
        if (!isOpen)
            return;
        var handleKeyDown = function (event) {
            switch (event.key) {
                case 'Escape':
                    onComplete();
                    break;
                case 'ArrowRight':
                case ' ':
                    event.preventDefault();
                    if (!isLastStep) {
                        setCurrentStep(function (prev) { return prev + 1; });
                    }
                    else {
                        onComplete();
                    }
                    break;
                case 'ArrowLeft':
                    event.preventDefault();
                    if (!isFirstStep) {
                        setCurrentStep(function (prev) { return prev - 1; });
                    }
                    break;
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return function () { return document.removeEventListener('keydown', handleKeyDown); };
    }, [isOpen, isFirstStep, isLastStep, onComplete]);
    var handleNext = function () {
        var _a;
        if (((_a = step.action) === null || _a === void 0 ? void 0 : _a.type) === 'click' && step.action.element) {
            var element = document.querySelector(step.action.element);
            if (element) {
                element.click();
            }
        }
        if (isLastStep) {
            onComplete();
        }
        else {
            setCurrentStep(function (prev) { return prev + 1; });
        }
    };
    var handlePrevious = function () {
        if (!isFirstStep) {
            setCurrentStep(function (prev) { return prev - 1; });
        }
    };
    var handleSkip = function () {
        onComplete();
    };
    if (!isOpen)
        return null;
    return (_jsxs("div", __assign({ className: "fixed inset-0 z-50" }, { children: [_jsx("div", { ref: overlayRef, className: "absolute inset-0 bg-black/50 transition-opacity duration-300", onClick: onComplete, style: isMobile ? { touchAction: 'none' } : undefined }), highlightPosition.width > 0 && (_jsx("div", { className: "absolute border-2 border-primary rounded-lg shadow-lg bg-primary/10 transition-all duration-300", style: {
                    left: highlightPosition.x,
                    top: highlightPosition.y,
                    width: highlightPosition.width,
                    height: highlightPosition.height,
                    pointerEvents: 'none'
                } })), _jsxs("div", __assign({ className: cn("absolute bg-background border border-border rounded-lg shadow-xl z-10 transition-all duration-300", isMobile
                    ? "max-w-[280px] w-[calc(100vw-2rem)] mx-4"
                    : "max-w-sm"), style: {
                    left: tooltipPosition.x,
                    top: tooltipPosition.y,
                    transform: "translate(".concat(tooltipPosition.transformX, ", ").concat(tooltipPosition.transformY, ")")
                } }, { children: [_jsxs("div", __assign({ className: cn("flex items-center justify-between border-b border-border", isMobile ? "p-3" : "p-4") }, { children: [_jsxs("div", __assign({ className: "flex items-center space-x-2" }, { children: [_jsx("div", __assign({ className: "w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center" }, { children: _jsx(Lightbulb, { className: "w-4 h-4 text-primary" }) })), _jsxs("div", { children: [_jsx("h3", __assign({ className: cn("font-semibold text-foreground", isMobile ? "text-base" : "text-lg") }, { children: step.title })), _jsxs("div", __assign({ className: "flex items-center space-x-2 text-xs text-muted-foreground" }, { children: [_jsxs("span", { children: ["Step ", currentStep + 1, " of ", steps.length] }), _jsxs(Badge, __assign({ variant: "outline", className: "px-1 py-0 text-xs" }, { children: [Math.round(((currentStep + 1) / steps.length) * 100), "%"] }))] }))] })] })), _jsx(Button, __assign({ variant: "ghost", size: isMobile ? "default" : "sm", onClick: onComplete, className: cn("p-0", isMobile && "min-h-[44px] min-w-[44px]") }, { children: _jsx(X, { className: isMobile ? "w-5 h-5" : "w-4 h-4" }) }))] })), _jsx("div", __assign({ className: cn(isMobile ? "p-3" : "p-4") }, { children: _jsx("p", __assign({ className: cn("text-foreground leading-relaxed", isMobile ? "text-sm" : "text-sm") }, { children: step.content })) })), _jsxs("div", __assign({ className: cn("flex items-center justify-between border-t border-border bg-muted/30", isMobile ? "p-3 flex-col space-y-3" : "p-4") }, { children: [_jsxs("div", __assign({ className: cn("flex items-center", isMobile ? "w-full justify-between" : "space-x-2") }, { children: [!isFirstStep && (_jsxs(Button, __assign({ variant: "outline", size: isMobile ? "default" : "sm", onClick: handlePrevious, className: cn("text-muted-foreground", isMobile && "flex-1 min-h-[44px]") }, { children: [_jsx(ChevronLeft, { className: "w-4 h-4 mr-1" }), !isMobile && "Back"] }))), _jsxs(Button, __assign({ variant: "ghost", size: isMobile ? "default" : "sm", onClick: handleSkip, className: cn("text-muted-foreground hover:text-foreground", isMobile && "flex-1 min-h-[44px]") }, { children: [_jsx(SkipForward, { className: "w-4 h-4 mr-1" }), !isMobile && "Skip Tour", isMobile && "Skip"] }))] })), _jsx(Button, __assign({ onClick: handleNext, size: isMobile ? "default" : "sm", className: cn("bg-primary text-primary-foreground hover:bg-primary/90", isMobile && "w-full min-h-[44px]") }, { children: isLastStep ? (_jsxs(_Fragment, { children: [_jsx(Target, { className: "w-4 h-4 mr-2" }), "Finish"] })) : (_jsxs(_Fragment, { children: ["Next", _jsx(ChevronRight, { className: "w-4 h-4 ml-1" })] })) }))] })), _jsx("div", __assign({ className: "h-1 bg-muted" }, { children: _jsx("div", { className: "h-full bg-primary transition-all duration-300", style: { width: "".concat(((currentStep + 1) / steps.length) * 100, "%") } }) })), !isMobile && (_jsx("div", __assign({ className: "px-4 py-2 text-xs text-muted-foreground bg-muted/50 border-t border-border" }, { children: _jsxs("div", __assign({ className: "flex items-center justify-center space-x-4" }, { children: [_jsx("span", { children: "\u2190 \u2192 Navigate" }), _jsx("span", { children: "Space Next" }), _jsx("span", { children: "Esc Exit" })] })) })))] }))] })));
};
// Hook for managing tour state
export var useGuidedTour = function (tourId) {
    if (tourId === void 0) { tourId = 'default'; }
    var _a = useState(false), isOpen = _a[0], setIsOpen = _a[1];
    var _b = useState(false), hasCompleted = _b[0], setHasCompleted = _b[1];
    useEffect(function () {
        // Check if user has completed this tour
        var completed = localStorage.getItem("tour_completed_".concat(tourId));
        setHasCompleted(completed === 'true');
    }, [tourId]);
    var startTour = function () {
        setIsOpen(true);
    };
    var closeTour = function () {
        setIsOpen(false);
    };
    var completeTour = function () {
        setIsOpen(false);
        setHasCompleted(true);
        localStorage.setItem("tour_completed_".concat(tourId), 'true');
    };
    var resetTour = function () {
        setHasCompleted(false);
        localStorage.removeItem("tour_completed_".concat(tourId));
    };
    return {
        isOpen: isOpen,
        hasCompleted: hasCompleted,
        startTour: startTour,
        closeTour: closeTour,
        completeTour: completeTour,
        resetTour: resetTour
    };
};
// Export default tour steps for tabs
export { TAB_TOUR_STEPS };
